import React, { Dispatch, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { DeleteRounded } from '@material-ui/icons';
import {
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Formik, Form } from 'formik';

import { DatePickers } from 'components';
import { Network, SharesItemFragment, useUpdateScheduledSharesMutation } from 'api';
import { useNotify } from 'hooks';
import { getCommentCharLimit } from 'features/posting/helpers';
import { useUpdateSharesItems } from 'features/activity/updaters';
import CommentSection from 'features/activity/components/CommentSection';
import { Action } from '../components/Actions';

interface IProps {
  open: boolean;
  shareItem: SharesItemFragment;
  setAction: Dispatch<Action>;
}

const useStyles = makeStyles(theme => ({
  minFlatPickr: {
    boxShadow: `0 2px 15px ${theme.palette.grey[500]}`,
  },
}));

const EditShareDialog: FC<IProps> = ({ open, shareItem, setAction }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const notify = useNotify();
  const { id, network_id, network, content, user_commentary, share_at } = shareItem;
  const [getEditShare, setEditShare] = useState({
    user_commentary,
    share_at,
  } as Partial<SharesItemFragment>);

  function handleNotification() {
    notify.info({ message: t('sharing:shareUpdated') });
  }

  const updateSharesItemsOptions = useUpdateSharesItems({
    scheduled: true,
    group_by_session: false,
    data: {
      limit: 25,
    },
  });

  const { mutate } = useUpdateScheduledSharesMutation({
    onSuccess: handleNotification,
    ...updateSharesItemsOptions,
  });

  function updateScheduledShare() {
    mutate({
      data: {
        items: [
          {
            id,
            network_id,
            ...getEditShare,
          },
        ],
      },
    });
    setAction(Action.None);
  }

  function handleDateChange(newTime: number) {
    setEditShare({
      ...getEditShare,
      share_at: newTime,
    });
  }

  function handleCancel() {
    setEditShare({
      user_commentary,
      share_at,
    });
    setAction(Action.None);
  }

  function renderTitle() {
    return (
      <Grid item>
        <Typography align="left" variant="h6">
          {t('sharing:editScheduledShare')}
        </Typography>
      </Grid>
    );
  }

  function renderCalendar() {
    return (
      <Grid item>
        <DatePickers.DateTime
          classes={{ minFlatPickr: classes.minFlatPickr }}
          anchorEl={null}
          enableTime
          value={getEditShare.share_at}
          onClose={() => null}
          onSave={() => null}
          onChange={handleDateChange}
        />
      </Grid>
    );
  }

  function renderEditControls() {
    return (
      <Grid container>
        <Grid item xs>
          <Tooltip placement="top" title={t('common:delete')}>
            <IconButton onClick={() => setAction(Action.Delete)}>
              <DeleteRounded />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item>
          <Grid container spacing={2}>
            <Grid item>
              <Button variant="contained" size="large" onClick={handleCancel}>
                {t('common:cancel')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                disabled={
                  getEditShare.user_commentary.length >
                  getCommentCharLimit(network as Network, content)
                }
                type="submit"
                variant="contained"
                size="large"
                color="primary"
              >
                {t('common:save')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogContent>
        <Formik
          initialValues={{ user_commentary: getEditShare.user_commentary }}
          onSubmit={updateScheduledShare}
        >
          <Form>
            <Grid container spacing={2} direction="column">
              {renderTitle()}
              <Grid item>
                <Grid container spacing={4}>
                  {renderCalendar()}
                  <CommentSection
                    shareItem={shareItem}
                    setEditShare={setEditShare}
                    getEditShare={getEditShare}
                  />
                </Grid>
              </Grid>
              <Grid item>{renderEditControls()}</Grid>
            </Grid>
          </Form>
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditShareDialog;
